import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";

import { NavArrow } from "./NavArrow";

import { pxToRem } from "@otta/design-tokens";
import { PageProgressIndicator } from "@otta/design";

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  max-width: 100%;
`;

const CardsContainer = styled.div`
  display: flex;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  gap: ${pxToRem(4)};
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PageProgressContainer = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  z-index: 0;
`;

const CardWrapper = styled.div`
  width: 100%;
  flex: 0 0 100%;
  scroll-snap-align: center;
`;

export function Carousel({
  cards,
  displayDots = false,
}: {
  cards: JSX.Element[];
  displayDots?: boolean;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [atCarouselStart, setAtCarouselStart] = useState(true);
  const [atCarouselEnd, setAtCarouselEnd] = useState(false);
  const [currentCard, setCurrentCard] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (wrapperRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = wrapperRef.current;
        setAtCarouselStart(scrollLeft === 0);
        setAtCarouselEnd(scrollLeft === scrollWidth - clientWidth);
        setCurrentCard(
          wrapperRef.current
            ? Math.round(
                (wrapperRef.current?.scrollLeft +
                  wrapperRef.current.clientWidth) /
                  wrapperRef.current.clientWidth
              )
            : 0
        );
      }
    };

    wrapperRef.current?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      wrapperRef.current?.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    wrapperRef.current?.scrollTo?.(0, 0);
  }, [cards]);

  const handleNext = () => {
    wrapperRef.current?.scrollTo({
      left: wrapperRef.current.scrollLeft + wrapperRef.current.clientWidth,
      behavior: "smooth",
    });
  };

  const handlePrevious = () => {
    wrapperRef.current?.scrollTo({
      left: wrapperRef.current.scrollLeft - wrapperRef.current.clientWidth,
      behavior: "smooth",
    });
  };

  return (
    <>
      <Wrapper>
        <NavArrow
          onClick={handlePrevious}
          direction="left"
          displayArrow={!atCarouselStart && cards.length > 1}
          testId="previous-button"
        />
        <CardsContainer tabIndex={0} ref={wrapperRef}>
          {cards.map((card, index) => (
            <CardWrapper
              key={`carousel-card-${index}`}
              className={`carousel-card-${
                Math.max(1, Math.min(currentCard, cards.length)) - 1 == index
                  ? "visible"
                  : "hidden"
              }`}
            >
              {card}
            </CardWrapper>
          ))}
        </CardsContainer>
        <NavArrow
          onClick={handleNext}
          direction="right"
          displayArrow={!atCarouselEnd && cards.length > 1}
          testId="next-button"
        />
      </Wrapper>
      {displayDots && (
        <PageProgressContainer>
          <PageProgressIndicator
            total={cards.length}
            current={currentCard}
            progress={currentCard}
          />
        </PageProgressContainer>
      )}
    </>
  );
}
